import { useEffect, useState } from "react";
import { chatClient } from "@core/clients";
import { supabaseClient } from "../../parhato2/src/shared/supabase";

const REFRESH_LEAD_MS = 5 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 10 * 1000;
const RETRY_DELAY_MS = 30 * 1000;

// Fetches a short-lived JWT, pushes it into supabaseClient.realtime and
// schedules the next refresh ~5 minutes before expiry. State is local to the
// component — when `enabled` flips to false (e.g. logout), cleanup cancels the
// timer and resets `isReady`.
const useChatRealtimeAuth = (enabled: boolean): boolean => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsReady(false);
      return;
    }

    let cancelled = false;
    let timer: number | undefined;

    const applyAuth = async () => {
      try {
        const { token, expiresAt } = await chatClient.getRealtimeSessionToken();
        if (cancelled) return;

        await supabaseClient.realtime.setAuth(token);
        if (cancelled) return;

        setIsReady(true);
        const expiresAtMs = expiresAt * 1000;
        const delayMs = Math.max(expiresAtMs - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
        timer = window.setTimeout(applyAuth, delayMs);
      } catch (error) {
        if (cancelled) return;
        console.error("[chat-realtime-auth] failed to obtain token", error);
        setIsReady(false);
        timer = window.setTimeout(applyAuth, RETRY_DELAY_MS);
      }
    };

    applyAuth();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enabled]);

  return isReady;
};

export default useChatRealtimeAuth;
